import _ from 'lodash';
import { v4 } from 'uuid';
import {
  denormalize,
  normalizeById,
  normalizeUserProfile,
  upsertEntity,
  upsertEntities,
  deleteEntityById,
  updateEntityAttribute,
  mergeEntities
} from 'shared/lib/normalizer';
import { PROFILE_PARSER } from 'shared/constants';
import reducerWithFlags from 'shared/lib/reducerWithFlags';
import { ADDED_APPLICATION } from './applications';

const PROFILE_UPDATE = 'PROFILE_UPDATE';
const PROFILE_UPDATING = 'PROFILE_UPDATING';
export const PROFILE_UPDATED = 'PROFILE_UPDATED';
const PROFILE_UPDATE_FAILED = 'PROFILE_UPDATE_FAILED';
const PROFILE_ADD_ROLE = 'PROFILE_ADD_ROLE';
const PROFILE_ADD_SKILL = 'PROFILE_ADD_SKILL';
const PROFILE_ADD_LANGUAGE = 'PROFILE_ADD_LANGUAGE';
const PROFILE_ATTACHMENT_UPLOAD = 'PROFILE_ATTACHMENT_UPLOAD';
const PROFILE_ATTACHMENT_UPLOADED = 'PROFILE_ATTACHMENT_UPLOADED';
const PROFILE_ATTACHMENT_UPLOADING = 'PROFILE_ATTACHMENT_UPLOADING';
const PROFILE_ATTACHMENT_FINALLY = 'PROFILE_ATTACHMENT_FINALLY';
const PROFILE_ATTACHMENT_UPDATE = 'PROFILE_ATTACHMENT_UPDATE';
const PROFILE_ATTACHMENT_UPDATED = 'PROFILE_ATTACHMENT_UPDATED';
const PROFILE_ATTACHMENT_UPDATING = 'PROFILE_ATTACHMENT_UPDATING';
const PROFILE_ATTACHMENT_DELETE = 'PROFILE_ATTACHMENT_DELETE';
const PROFILE_ATTACHMENT_DELETED = 'PROFILE_ATTACHMENT_DELETED';
const PROFILE_ATTACHMENT_DELETING = 'PROFILE_ATTACHMENT_DELETING';
const PROFILE_ATTACHMENT_DELETE_FAILED = 'PROFILE_ATTACHMENT_DELETE_FAILED';
const PROFILE_PARSE_UPLOAD = 'PROFILE_PARSE_UPLOAD';
const PROFILE_PARSE_UPLOADED = 'PROFILE_PARSE_UPLOADED';
const PROFILE_PARSE_UPLOADING = 'PROFILE_PARSE_UPLOADING';
const PROFILE_PARSE_FINALLY = 'PROFILE_PARSE_FINALLY';

const PROFILE_LINK_ADD = 'PROFILE_LINK_ADD';
const PROFILE_LINK_ADDED = 'PROFILE_LINK_ADDED';
const PROFILE_LINK_ADDING = 'PROFILE_LINK_ADDING';
const PROFILE_LINK_ADD_FINALLY = 'PROFILE_LINK_ADD_FINALLY';
const PROFILE_LINK_UPDATE = 'PROFILE_LINK_UPDATE';
const PROFILE_LINK_UPDATED = 'PROFILE_LINK_UPDATED';
const PROFILE_LINK_UPDATING = 'PROFILE_LINK_UPDATING';

const PROFILE_LINK_DELETE = 'PROFILE_LINK_DELETE';
const PROFILE_LINK_DELETED = 'PROFILE_LINK_DELETED';
const PROFILE_LINK_DELETING = 'PROFILE_LINK_DELETING';
const PROFILE_LINK_DELETE_FAILED = 'PROFILE_LINK_DELETE_FAILED';

export const PROFILE_PARSE_SUCCESS = 'PROFILE_PARSE_SUCCESS@UPDATE';
export const PROFILE_PARSE_FAILED = 'PROFILE_PARSE_FAILED@UPDATE';

const normalizeProfile = ({ firstname, lastname, email, createdAt, ...profile }) => normalizeUserProfile(profile);

export const initialState = {
  // fields: [],
  roles: normalizeById([]),
  skills: normalizeById([]),
  languages: normalizeById([]),
  works: normalizeById([]),
  educations: normalizeById([]),
  attachments: normalizeById([]),
  links: normalizeById([]),
};

export default reducerWithFlags((state = initialState, action) => {
  switch (action.type) {
    case PROFILE_UPDATED:
      return normalizeProfile(action.result);
    case PROFILE_ADD_ROLE:
      return {
        ...state,
        roles: upsertEntity(state.roles, action.payload, action.payload.roleId)
      };
    case PROFILE_ADD_SKILL:
      return {
        ...state,
        skills: upsertEntity(state.skills, action.payload, action.payload.skillId)
      };
    case PROFILE_ADD_LANGUAGE:
      return {
        ...state,
        languages: upsertEntity(state.languages, action.payload, action.payload.languageId)
      };
    case 'SKILL_REMOVED@UPDATE': {
      const removedSkillId = action.payload.skill ? action.payload.skill.id : null;
      if (!removedSkillId) {
        return state;
      }

      if (state.skills.ids.includes(removedSkillId)) {
        return {
          ...state,
          skills: deleteEntityById(state.skills, removedSkillId),
          works: {
            ids: state.works.ids,
            entities: _.keyBy(
              state.works.ids.map(id => state.works.entities[id]).map(work => ({
                ...work,
                skillIds: (work.skillIds || []).filter(id => id !== removedSkillId)
              })), 'id'
            )
          }
        };
      }

      if (state.ignoredSkills.ids.includes(removedSkillId)) {
        return {
          ...state,
          ignoredSkills: deleteEntityById(state.ignoredSkills, removedSkillId)
        };
      }

      return state;
    }
    case 'SKILL_APPROVED@UPDATE': {
      const approvedSkillId = action.payload.skill ? action.payload.skill.id : null;
      const approvedSkillTitle = action.payload.skill ? action.payload.skill.title : null;
      if (!approvedSkillId) {
        return state;
      }

      if (state.skills.ids.includes(approvedSkillId)) {
        return {
          ...state,
          skills: {
            ...state.skills,
            entities: {
              ...state.skills.entities,
              [approvedSkillId]: {
                ...state.skills.entities[approvedSkillId],
                title: approvedSkillTitle || state.skills.entities[approvedSkillId].title,
                approved: true
              }
            }
          }
          // skills: updateEntityAttribute(state.skills, approvedSkillId, 'approved', true)
        };
      }

      if (state.ignoredSkills.ids.includes(approvedSkillId)) {
        return {
          ...state,
          ignoredSkills: updateEntityAttribute(state.ignoredSkills, approvedSkillId, 'approved', true)
        };
      }

      return state;
    }
    case 'SKILL_MERGED@UPDATE': {
      const mergedSkill = action.payload.skill;
      const newSkill = action.payload.mergedTo;
      if (!(mergedSkill && mergedSkill.id && newSkill && newSkill.id)) {
        return state;
      }

      if (state.skills.ids.includes(mergedSkill.id)) {
        return {
          ...state,
          skills: mergeEntities(state.skills, mergedSkill, newSkill, 'id', 'skillId', ['slug', 'title', 'approved']),
          works: {
            ids: state.works.ids,
            entities: _.keyBy(
              state.works.ids.map(id => state.works.entities[id]).map(work => ({
                ...work,
                skillIds: (work.skillIds || []).some(id => id === mergedSkill.id)
                  ? _.uniq((work.skillIds || []).filter(id => id !== mergedSkill.id).concat(newSkill.id))
                  : work.skillIds
              })), 'id'
            )
          }
        };
      }

      if (state.ignoredSkills.ids.includes(mergedSkill.id)) {
        return {
          ...state,
          ignoredSkills: mergeEntities(state.ignoredSkills, mergedSkill, newSkill, 'id', 'skillId', ['slug', 'title', 'approved'])
        };
      }

      return state;
    }
    case 'SKILL_REPLACED@UPDATE': {
      const prevSkill = action.payload.skill;
      const newSkills = action.payload.replacedWith;
      if (!(prevSkill && prevSkill.id && newSkills && newSkills.length > 0)) {
        return state;
      }
      const newSkillIds = newSkills.map(skill => skill.id);

      if (state.skills.ids.includes(prevSkill.id)) {
        const prevData = _.pick(state.skills.entities[prevSkill.id] || {}, ['levelId', 'levelIdExp', 'experience']);
        const { [prevSkill.id]: removed, ...rest } = state.skills.entities;
        return {
          ...state,
          skills: {
            ids: _.uniq(state.skills.ids.filter(id => id !== prevSkill.id).concat(newSkillIds)),
            entities: {
              ...rest,
              ...(newSkills.reduce((acc, curr) => {
                acc[curr.id] = {
                  ...curr,
                  skillId: curr.id,
                  ...prevData,
                  levelId: state.skills.entities[curr.id] && state.skills.entities[curr.id].levelId && !state.skills.entities[curr.id].soft
                    ? state.skills.entities[curr.id].levelId
                    : prevData.levelId
                };

                delete acc.id;
                return acc;
              }, {}))
            }
          },
          works: {
            ids: state.works.ids,
            entities: _.keyBy(
              state.works.ids.map(id => state.works.entities[id]).map(work => ({
                ...work,
                skillIds: (work.skillIds || []).some(id => id === prevSkill.id)
                  ? _.uniq((work.skillIds || []).filter(id => id !== prevSkill.id).concat(newSkillIds))
                  : work.skillIds
              })), 'id'
            )
          }
        };
      }

      if (state.ignoredSkills.ids.includes(prevSkill.id)) {
        const { [prevSkill.id]: removed, ...rest } = state.ignoredSkills.entities;
        return {
          ...state,
          ignoredSkills: {
            ids: state.ignoredSkills.ids.filter(id => id !== prevSkill.id),
            entities: rest
          }
        };
      }

      return state;
    }
    case PROFILE_ATTACHMENT_UPDATED:
    case PROFILE_ATTACHMENT_UPLOADED: {
      return {
        ...state,
        attachments: upsertEntity(state.attachments, action.result)
      };
    }
    case PROFILE_ATTACHMENT_DELETED: {
      return {
        ...state,
        attachments: deleteEntityById(state.attachments, action.result.id)
      };
    }
    case PROFILE_LINK_ADDED:
    case PROFILE_LINK_UPDATED: {
      return {
        ...state,
        links: upsertEntity(state.links, action.result)
      };
    }
    case PROFILE_LINK_DELETED: {
      return {
        ...state,
        links: deleteEntityById(state.links, action.result.id)
      };
    }
    case ADDED_APPLICATION: {
      return action.result.phone ? {
        ...state,
        phone: action.result.phone
      } : state;
    }

    case PROFILE_PARSE_UPLOADED:
      return {
        ...state,
        parses: [...state.parses, ...action.result],
      };

    case PROFILE_PARSE_SUCCESS: {
      const { request, result } = action.payload;
      return {
        ...normalizeProfile(result),
        parses: state.parses.map((parse) => {
          if (parse.id === request.id) {
            return { ...parse, ...request };
          }

          return parse;
        })
      };
    }

    case PROFILE_PARSE_FAILED: {
      const { request } = action.payload;
      return {
        ...state,
        parses: state.parses.map((parse) => {
          if (parse.id === request.id) {
            return { ...parse, ...request };
          }

          return parse;
        })
      };
    }

    case 'JOB_APPLICATION_CHANGE@UPDATE': {
      if (action.payload && action.payload.change && action.payload.change.attachments) {
        const attachments = action.payload.change.attachments || [];
        if (attachments.length > 0) {
          return {
            ...state,
            attachments: upsertEntities(state.attachments, attachments),
          };
        }
      }

      return state;
    }

    default:
      return state;
  }
},
[/* no loading actions */],
[
  PROFILE_UPDATING,
  PROFILE_ATTACHMENT_UPLOADING,
  PROFILE_PARSE_UPLOADING,
  PROFILE_ATTACHMENT_UPDATING,
  PROFILE_LINK_ADDING,
  PROFILE_LINK_UPDATING
],
'PROFILE_');

export const actions = {
  updateProfile(payload, onSuccess, onFailure) {
    return {
      type: PROFILE_UPDATE,
      types: [PROFILE_UPDATING, PROFILE_UPDATED, PROFILE_UPDATE_FAILED],
      callApi: client => client.post('/user/profile', { profile: payload }),
      onSuccess: onSuccess ? [onSuccess] : [],
      onFailure: onFailure ? [onFailure] : []
    };
  },
  addRoleToProfile(role) {
    return {
      type: PROFILE_ADD_ROLE,
      payload: role
    };
  },
  addSkillToProfile(skill) {
    return {
      type: PROFILE_ADD_SKILL,
      payload: skill
    };
  },
  addLanguageToProfile(language) {
    return {
      type: PROFILE_ADD_LANGUAGE,
      payload: language
    };
  },
  uploadAttachment(attachment, file, onSuccess, onFailure, progressCallback, abortCallback) {
    return {
      type: PROFILE_ATTACHMENT_UPLOAD,
      types: [PROFILE_ATTACHMENT_UPLOADING, PROFILE_ATTACHMENT_UPLOADED, null, PROFILE_ATTACHMENT_FINALLY],
      callApi: client => client.post(
        '/user/profile/attachments', { attachment }, {}, [{ name: file.name, file }],
        progressCallback,
        abortCallback
      ),
      onSuccess: onSuccess ? [onSuccess] : [],
      onFailure: onFailure ? [onFailure] : []
    };
  },
  updateAttachment(attachment, onSuccess, onFailure) {
    return {
      type: PROFILE_ATTACHMENT_UPDATE,
      types: [PROFILE_ATTACHMENT_UPDATING, PROFILE_ATTACHMENT_UPDATED, null, PROFILE_ATTACHMENT_FINALLY],
      callApi: client => client.post(`/user/profile/attachments/${attachment.id}`, { attachment }),
      onSuccess: onSuccess ? [onSuccess] : [],
      onFailure: onFailure ? [onFailure] : []
    };
  },
  deleteAttachment(attachmentId, onSuccess) {
    return {
      type: PROFILE_ATTACHMENT_DELETE,
      types: [PROFILE_ATTACHMENT_DELETING, PROFILE_ATTACHMENT_DELETED, PROFILE_ATTACHMENT_DELETE_FAILED],
      callApi: client => client.delete(`/user/profile/attachments/${attachmentId}`),
      onSuccess: onSuccess ? [onSuccess] : []
    };
  },
  addLink(link, onSuccess, onFailure) {
    return {
      type: PROFILE_LINK_ADD,
      types: [PROFILE_LINK_ADDING, PROFILE_LINK_ADDED, null, PROFILE_LINK_ADD_FINALLY],
      callApi: client => client.post('/user/profile/links', { link }),
      onSuccess: onSuccess ? [onSuccess] : [],
      onFailure: onFailure ? [onFailure] : []
    };
  },
  updateLink(link, onSuccess, onFailure) {
    return {
      type: PROFILE_LINK_UPDATE,
      types: [PROFILE_LINK_UPDATING, PROFILE_LINK_UPDATED, null, PROFILE_LINK_ADD_FINALLY],
      callApi: client => client.post(`/user/profile/links/${link.id}`, { link }),
      onSuccess: onSuccess ? [onSuccess] : [],
      onFailure: onFailure ? [onFailure] : []
    };
  },
  deleteLink(linkId, onSuccess) {
    return {
      type: PROFILE_LINK_DELETE,
      types: [PROFILE_LINK_DELETING, PROFILE_LINK_DELETED, PROFILE_LINK_DELETE_FAILED],
      callApi: client => client.delete(`/user/profile/links/${linkId}`),
      onSuccess: onSuccess ? [onSuccess] : []
    };
  },
  parseProfile(data, file, onSuccess, onFailure, progressCallback, abortCallback) {
    return {
      type: PROFILE_PARSE_UPLOAD,
      types: [PROFILE_PARSE_UPLOADING, PROFILE_PARSE_UPLOADED, null, PROFILE_PARSE_FINALLY],
      callApi: client => client.post(
        '/user/profile/parse', data, {}, file ? [{ name: file.name, file }] : undefined,
        progressCallback,
        abortCallback
      ),
      onSuccess: onSuccess ? [onSuccess] : [],
      onFailure: onFailure ? [onFailure] : []
    };
  },
};

const isParseTypeInProgress = (state, type) => {
  if (!state.auth.loggedIn) {
    return false;
  }

  return !!(state.profile.parses || []).find(parse => parse.type === type && !parse.finishedAt);
};

export const selectors = {
  getUserProfile(state) {
    return state.profile;
  },
  hasInsufficientProfile(state) {
    return state.auth.loggedIn
      && !state.app.appLoading
      && state.profile
      && state.profile.skills
      && state.profile.skills.ids.length === 0;
  },
  getRolesForMatching(state) {
    if (!state.auth.loggedIn) {
      return {};
    }

    return _.values(state.profile.roles.entities).reduce((acc, curr) => {
      acc[curr.slug] = curr.levelId;

      return acc;
    }, {});
  },
  getSkillsForMatching(state) {
    if (!state.auth.loggedIn) {
      return {};
    }
    return _.values(state.profile.skills.entities).reduce((acc, curr) => {
      if (!acc[curr.slug]) {
        acc[curr.slug] = curr.levelId;
      } else if (acc[curr.slug] && acc[curr.slug] < curr.levelId) {
        acc[curr.slug] = curr.levelId;
      }

      return acc;
    }, {});
  },
  getLanguagesForMatching(state) {
    if (!state.auth.loggedIn) {
      return {};
    }
    return _.values(state.profile.languages.entities).reduce((acc, curr) => {
      acc[curr.slug] = curr.levelId;

      return acc;
    }, {});
  },
  getIgnoredSkillsMap(state) {
    if (!(state.auth.loggedIn && state.profile.ignoredSkills)) {
      return {};
    }

    return state.profile.ignoredSkills.entities;
  },
  getIgnoredSkillsSlugsMap(state) {
    if (!state.auth.loggedIn) {
      return {};
    }

    return state.profile.ignoredSkills.slugsMap || {};
  },
  getLocations(state) {
    if (!state.auth.loggedIn) {
      return [];
    }

    return state.profile.locations.ids.map(id => state.profile.locations.entities[id]);
  },
  getHighestEducationLevel(state) {
    if (!state.auth.loggedIn) {
      return [];
    }

    return _.values(state.profile.educations.entities).map(edu => edu.level).sort().reverse()[0] || 0;
  },
  getFieldsExperience(state) {
    if (!state.auth.loggedIn) {
      return {};
    }

    return _.values(state.profile.fields.entities).reduce((acc, curr) => {
      acc[curr.fieldId] = curr.experience;
      return acc;
    }, {});
  },
  getRolesExperience(state) {
    if (!state.auth.loggedIn) {
      return {};
    }

    return _.values(state.profile.roles.entities).reduce((acc, curr) => {
      acc[curr.roleId] = curr.experience;
      return acc;
    }, {});
  },
  getAttachments(state) {
    if (!state.auth.loggedIn) {
      return [];
    }

    return denormalize(state.profile.attachments);
  },
  getWillingToRelocate(state) {
    if (!state.auth.loggedIn) {
      return false;
    }

    return state.profile.relocation;
  },
  getAnonymization(state) {
    if (!state.auth.loggedIn) {
      return 0;
    }

    return state.profile.anonymization;
  },
  isCvParseInProgress(state) {
    return isParseTypeInProgress(state, PROFILE_PARSER.CV);
  },
  isLinkedinParseInProgress(state) {
    return isParseTypeInProgress(state, PROFILE_PARSER.LINKEDIN);
  },
  isAnyParseInProgress(state) {
    if (!state.auth.loggedIn) {
      return false;
    }

    return !!(state.profile.parses || []).find(parse => !parse.finishedAt);
  }
};

export const helpers = {
  normalizeProfile,
  denormalizeProfile(profile, keepSkills = false) {
    const {
      id, userId, createdAt, updatedAt, fields, loading, submitting, photo, monthlyRate,
      attachments, links, primaryFieldId, parses, type, ...rest
    } = profile;
    return {
      ...rest,
      primaryFieldId: ~~primaryFieldId,
      homeoffice: 'homeoffice' in profile && profile.homeoffice !== null && /^[0-9]{1}/.test(profile.homeoffice.toString()) ? profile.homeoffice : undefined,
      remoteWork: profile.remoteWork || undefined,
      roles: _.values(profile.roles.entities).map(role => _.pick(role, ['roleId'])),
      skills: keepSkills ? _.values(profile.skills.entities) : _.values(profile.skills.entities).filter(skill => !skill.isSuggest).map(skill => _.pick(skill, ['skillId', 'levelId'])),
      ignoredSkills: _.values(profile.ignoredSkills.entities).filter(skill => !skill.isSuggest).map(skill => _.pick(skill, ['skillId'])),
      languages: _.values(profile.languages.entities).map(language => _.pick(language, ['languageId', 'levelId'])),
      newSkills: _.values(profile.skills.entities).filter(skill => skill.isSuggest).map(skill => _.pick(skill, ['skillId', 'title', 'levelId'])),
      newIgnoredSkills: _.values(profile.ignoredSkills.entities).filter(skill => skill.isSuggest).map(skill => _.pick(skill, ['skillId', 'title'])),
      works: _.values(profile.works.entities).map(work => _.omit({ ...work, id: work.id === 'new' ? v4() : work.id }, ['userProfileId'])),
      educations: _.values(profile.educations.entities).map(edu => _.omit({ ...edu, id: edu.id === 'new' ? v4() : edu.id }, ['userProfileId'])),
      employmentTypes: Object.keys(profile.employmentTypes).filter(et => profile.employmentTypes[et]),
      locations: _.values(profile.locations.entities)
    };
  },
  getRolesByField(normalizedRoles, fieldId) {
    return normalizedRoles.ids.map(roleId => normalizedRoles.entities[roleId]).filter(role => role.fieldId === fieldId);
  },
  getSkillsByField(normalizedSkills, fieldId) {
    return normalizedSkills.ids.map(skillId => normalizedSkills.entities[skillId]).filter(skill => skill.fieldId === fieldId);
  },
};
